import React from 'react';
import { EmptyState, MaterialIcon } from '../../ui';
import { REVIEW_REASONS, getReviewQueue, markReviewed, reopen } from '../../../services/ragReviewService';

const PAGE_SIZE = 25;

const STATUSES = [
  { key: 'open', label: 'Open' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'all', label: 'All' },
];

const REASON_BY_KEY = Object.fromEntries(REVIEW_REASONS.map((r) => [r.key, r]));

const ROLE_LABELS = { admin: 'Admin', board: 'Board', acc_member: 'ACC', management: 'Management', member: 'Member' };

const formatExact = (value) => (value
  ? new Date(value).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  })
  : '');

const pillClass = (active) => `px-3.5 py-1.5 text-xs font-bold rounded-full border transition-colors ${
  active ? 'bg-secondary/10 border-secondary/40 text-secondary' : 'bg-white border-amber-100/60 text-slate-600 hover:border-secondary/40'
}`;

const ReviewRow = ({ row, hoaId, onChanged, onCreateOverride, onViewThread }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [note, setNote] = React.useState(row.review_note || '');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const sources = Array.isArray(row.sources) ? row.sources : [];
  const isReviewed = Boolean(row.reviewed_at);

  const act = async (fn) => {
    setSaving(true);
    setError('');
    try {
      const ok = await fn({ hoaId, logId: row.id, note });
      if (!ok) throw new Error('This answer could not be updated.');
      onChanged();
    } catch (err) {
      setError(err?.message || 'This answer could not be updated.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="bg-white rounded-2xl border border-amber-100/40 p-4 sm:p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-bold text-primary-dark">{row.asker}</span>
        {row.user_role && (
          <span className="px-2.5 py-0.5 rounded-full bg-section-bg text-slate-600 text-[10px] font-bold uppercase tracking-wider">
            {ROLE_LABELS[row.user_role] || row.user_role}
          </span>
        )}
        <span className="text-xs text-slate-500">{formatExact(row.asked_at)}</span>
        <span className="flex flex-wrap gap-1.5 sm:ml-auto">
          {(row.reasons || []).map((r) => (
            <span key={r} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-secondary/10 text-secondary text-[10px] font-bold uppercase tracking-wider">
              <MaterialIcon name={REASON_BY_KEY[r]?.icon || 'flag'} size={12} />
              {REASON_BY_KEY[r]?.label || r}
            </span>
          ))}
        </span>
      </div>

      <p className="text-sm font-bold text-primary-dark break-words">{row.query}</p>

      {row.answer ? (
        <div>
          <p className={`text-sm text-slate-600 leading-relaxed whitespace-pre-line break-words bg-main-bg/60 rounded-2xl p-3 ${expanded ? '' : 'line-clamp-3'}`}>
            {row.answer}
          </p>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-secondary"
          >
            <MaterialIcon name={expanded ? 'expand_less' : 'expand_more'} size={16} />
            {expanded ? 'Show less' : 'Show full answer'}
          </button>
        </div>
      ) : (
        <p className="text-xs italic text-slate-500">No answer text was recorded.</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Sources</span>
        {sources.length === 0 ? (
          <span className="text-xs text-slate-500">No sources</span>
        ) : sources.map((s) => (
          <span key={s.document_id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-section-bg text-xs font-semibold text-primary-dark max-w-full">
            <MaterialIcon name="description" size={14} className="text-secondary shrink-0" />
            <span className="truncate">{s.title}</span>
          </span>
        ))}
        {row.thread_id && (
          <button
            type="button"
            onClick={() => onViewThread(row.thread_id, row.id)}
            className="inline-flex items-center gap-1 text-xs font-bold text-secondary sm:ml-auto"
          >
            <MaterialIcon name="forum" size={14} /> View thread
          </button>
        )}
      </div>

      <div className="border-t border-amber-100/40 pt-3 space-y-2">
        {isReviewed ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
            <p className="text-xs text-slate-600">
              <MaterialIcon name="check_circle" size={14} className="text-secondary align-middle mr-1" />
              Reviewed by <span className="font-bold">{row.reviewer || 'an admin'}</span> on {formatExact(row.reviewed_at)}
              {row.review_note && <span className="block mt-1 text-slate-500">“{row.review_note}”</span>}
            </p>
            <button
              type="button"
              disabled={saving}
              onClick={() => act(reopen)}
              className="border border-secondary text-secondary px-5 py-2 rounded-full font-bold text-xs hover:bg-secondary hover:text-white transition-all disabled:opacity-60 self-start"
            >
              Reopen
            </button>
          </div>
        ) : (
          <>
            <label className="block">
              <span className="sr-only">Review note</span>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optional): what was wrong, what you fixed…"
                className="w-full text-sm border border-amber-100/60 rounded-2xl px-4 py-2.5 bg-white focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20 resize-y"
              />
            </label>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => onCreateOverride(row.query)}
                className="border border-secondary text-secondary px-5 py-2 rounded-full font-bold text-xs hover:bg-secondary hover:text-white transition-all"
              >
                Create override
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => act(markReviewed)}
                className="bg-secondary text-white px-5 py-2 rounded-full font-bold text-xs hover:brightness-110 active:scale-95 transition-all disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Mark reviewed'}
              </button>
            </div>
          </>
        )}
        {error && <p className="text-xs text-red-700">{error}</p>}
      </div>
    </li>
  );
};

/**
 * "Needs review" queue: AI answers a member thumbed down, or that the AI could
 * not (fully) answer. Admin-only (admin_rag_review_queue).
 */
const ReviewQueue = ({ hoaId, includeAdmin, includeArchived, onCreateOverride, onViewThread, onChanged }) => {
  const [status, setStatus] = React.useState('open');
  const [reasons, setReasons] = React.useState(REVIEW_REASONS.map((r) => r.key));
  const [rows, setRows] = React.useState([]);
  const [nextCursor, setNextCursor] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState('');
  const [reloadKey, setReloadKey] = React.useState(0);
  const requestIdRef = React.useRef(0);

  React.useEffect(() => {
    if (!hoaId) return undefined;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const page = await getReviewQueue({
          hoaId, status, reasons, includeExcluded: includeAdmin, includeArchived, limit: PAGE_SIZE,
        });
        if (requestIdRef.current !== requestId) return;
        setRows(page.rows);
        setNextCursor(page.nextCursor);
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setRows([]);
        setNextCursor(null);
        setError(err?.message || 'The review queue could not be loaded.');
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    })();
    return undefined;
  }, [hoaId, status, reasons, includeAdmin, includeArchived, reloadKey]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    try {
      const page = await getReviewQueue({
        hoaId, status, reasons, includeExcluded: includeAdmin, includeArchived, before: nextCursor, limit: PAGE_SIZE,
      });
      if (requestIdRef.current !== requestId) return;
      setRows((current) => [...current, ...page.rows.filter((r) => !current.some((c) => c.id === r.id))]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(err?.message || 'More answers could not be loaded.');
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleReason = (key) => setReasons((current) => (
    current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
  ));

  const handleChanged = () => {
    setReloadKey((k) => k + 1);
    if (onChanged) onChanged();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Review status">
          {STATUSES.map((s) => (
            <button key={s.key} type="button" aria-pressed={status === s.key} onClick={() => setStatus(s.key)} className={pillClass(status === s.key)}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Review reasons">
          {REVIEW_REASONS.map((r) => (
            <button
              key={r.key}
              type="button"
              aria-pressed={reasons.includes(r.key)}
              onClick={() => toggleReason(r.key)}
              className={`inline-flex items-center gap-1 ${pillClass(reasons.includes(r.key))}`}
            >
              <MaterialIcon name={r.icon} size={14} /> {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm text-slate-500">Loading…</p>
      ) : error && rows.length === 0 ? (
        <p className="py-12 text-center text-sm text-red-700">{error}</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="task_alt"
          title="Nothing to review"
          body={status === 'reviewed'
            ? 'Answers you mark reviewed will appear here.'
            : "Answers members flag or that the AI couldn't answer will appear here."}
        />
      ) : (
        <>
          <ul className="space-y-3">
            {rows.map((row) => (
              <ReviewRow
                key={`${row.id}:${row.reviewed_at || 'open'}`}
                row={row}
                hoaId={hoaId}
                onChanged={handleChanged}
                onCreateOverride={onCreateOverride}
                onViewThread={onViewThread}
              />
            ))}
          </ul>
          {error && <p className="text-center text-sm text-red-700">{error}</p>}
          {nextCursor && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="border border-secondary text-secondary px-8 py-3 rounded-full font-bold text-sm hover:bg-secondary hover:text-white transition-all disabled:opacity-60"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewQueue;
